/**
 * Загрузка и скачивание вложений — общие для файлов задачи и комментариев.
 *
 * Загрузка в три шага (§14.2): ссылка → байты по HTTP → подтверждение.
 * Байты идут не по WebSocket: бинарный поток заблокировал бы сокет.
 */

import { useCallback } from "react";

import { resolveApiUrl, uploadSigned } from "@/shared/api/http";
import type { WsError } from "@taskflow/client-core";
import { toast } from "@/shared/ui";

import {
  useAttachmentDownloadUrlMutation,
  useCompleteAttachmentMutation,
  useRequestAttachmentUploadMutation,
} from "@taskflow/client-core/tasks";
import { attachmentMime, attachmentProblem } from "./attachmentRules";

export function useAttachmentFiles(taskKey: string) {
  const [requestUpload] = useRequestAttachmentUploadMutation();
  const [complete] = useCompleteAttachmentMutation();
  const [downloadUrl] = useAttachmentDownloadUrlMutation();

  /** true — файл приложен. Об ошибке сообщает сам: вызывающему достаточно результата. */
  const upload = useCallback(
    async (file: File, commentId?: string): Promise<boolean> => {
      // Проверки до загрузки — отказ после двадцати пяти мегабайт трафика бесполезен.
      const problem = attachmentProblem(file);
      if (problem !== null) {
        toast.warning(problem, file.name);
        return false;
      }
      try {
        const ticket = await requestUpload({
          key: taskKey,
          filename: file.name,
          mime: attachmentMime(file),
          size: file.size,
          ...(commentId === undefined ? {} : { commentId }),
        }).unwrap();
        await uploadSigned(ticket.uploadUrl, file);
        await complete({ id: ticket.attachmentId }).unwrap();
        return true;
      } catch (raw) {
        if (raw instanceof Error) toast.warning("Не удалось загрузить файл", raw.message);
        else toast.error(raw as WsError, "Не удалось загрузить файл");
        return false;
      }
    },
    [taskKey, requestUpload, complete],
  );

  const download = useCallback(
    async (id: string) => {
      try {
        const link = await downloadUrl({ id }).unwrap();
        const url = resolveApiUrl(link.url);
        // Ответ с Content-Disposition: attachment — браузер сохраняет файл,
        // оставаясь на странице.
        if (url !== null) window.location.assign(url);
      } catch (raw) {
        toast.error(raw as WsError, "Не удалось скачать файл");
      }
    },
    [downloadUrl],
  );

  return { upload, download };
}
